// ===== K-Tec Request Quote Modal =====
// Public-facing modal for product/package order requests.
// Only Name + Phone required. Attaches item details automatically.
import { useState } from 'react';
import { X, Phone, User, Mail, MapPin, MessageSquare, CheckCircle, Package, ShoppingBag } from 'lucide-react';
import { useWorkflowStore } from '../admin/store/workflowStore';

interface RequestQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id: string;
    name: string;
    type: 'product' | 'package';
    price: number;
    image?: string;
    source?: string;
  };
}

export default function RequestQuoteModal({ isOpen, onClose, item }: RequestQuoteModalProps) {
  const addOrder = useWorkflowStore(s => s.addOrder);
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    message: '',
    quantity: 1,
  });
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [refNumber, setRefNumber] = useState('');

  if (!isOpen) return null;

  const validate = () => {
    const e: { name?: string; phone?: string } = {};
    if (!form.customerName.trim()) e.name = 'Name is required';
    if (!form.customerPhone.trim()) e.phone = 'Phone number is required';
    else if (!/^[\d\s\+\-\(\)]{7,15}$/.test(form.customerPhone.trim())) e.phone = 'Enter a valid phone number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const orderId = addOrder({
      customerName: form.customerName.trim(),
      customerPhone: form.customerPhone.trim(),
      customerEmail: form.customerEmail.trim() || undefined,
      customerAddress: form.customerAddress.trim() || undefined,
      message: form.message.trim() || undefined,
      itemType: item.type,
      itemId: item.id,
      itemName: item.name,
      itemImage: item.image,
      itemPrice: item.price,
      quantity: form.quantity,
      orderSource: item.source || 'website',
      status: 'new',
    });

    // Get the reference number from the newly created order
    const orders = useWorkflowStore.getState().orders;
    const newOrder = orders.find(o => o.id === orderId);
    setRefNumber(newOrder?.referenceNumber || orderId);
    setSubmitted(true);
  };

  const handleClose = () => {
    setForm({ customerName: '', customerPhone: '', customerEmail: '', customerAddress: '', message: '', quantity: 1 });
    setErrors({});
    setSubmitted(false);
    setRefNumber('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1D1D1F] to-[#2D2D2F] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#7FA86A] rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Request a Quote</p>
              <p className="text-gray-400 text-xs">We'll contact you shortly</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          /* ── Success State ── */
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[#1D1D1F] mb-2">Order Received!</h3>
            <p className="text-gray-500 text-sm mb-4">
              Thank you <strong>{form.customerName}</strong>! Our team will contact you on <strong>{form.customerPhone}</strong> shortly.
            </p>
            <div className="bg-[#F5F5F7] rounded-xl p-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">Your Reference Number</p>
              <p className="text-lg font-bold text-[#7FA86A] font-mono">{refNumber}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-left mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-[#7FA86A]" />
                <span className="text-sm font-medium text-[#1D1D1F]">{item.name}</span>
              </div>
              <p className="text-xs text-gray-500 ml-6">Qty: {form.quantity} × Rs. {item.price.toLocaleString()}</p>
            </div>
            <button
              onClick={handleClose}
              className="w-full bg-[#7FA86A] text-white py-3 rounded-xl font-semibold hover:bg-[#e56d00] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          /* ── Form State ── */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Selected Item Preview */}
            <div className="bg-[#F5F5F7] rounded-xl p-3 flex items-center gap-3">
              {item.image ? (
                <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg" />
              ) : (
                <div className="w-12 h-12 bg-[#7FA86A]/10 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-[#7FA86A]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1D1D1F] truncate">{item.name}</p>
                <p className="text-xs text-gray-500">Rs. {item.price.toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setForm(f => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))}
                  className="w-7 h-7 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-bold">−</button>
                <span className="w-6 text-center text-sm font-semibold">{form.quantity}</span>
                <button type="button" onClick={() => setForm(f => ({ ...f, quantity: f.quantity + 1 }))}
                  className="w-7 h-7 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-bold">+</button>
              </div>
            </div>

            {/* Required Fields */}
            <div>
              <label className="block text-xs font-semibold text-[#1D1D1F] mb-1.5">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={form.customerName}
                  onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                  placeholder="Your full name"
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30 ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                />
              </div>
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1D1D1F] mb-1.5">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  value={form.customerPhone}
                  onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))}
                  placeholder="076 282 9800"
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30 ${errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
            </div>

            {/* Optional Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#1D1D1F] mb-1.5">Email <span className="text-gray-400">(optional)</span></label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="email" value={form.customerEmail}
                    onChange={e => setForm(f => ({ ...f, customerEmail: e.target.value }))}
                    placeholder="email@example.com"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#1D1D1F] mb-1.5">Area <span className="text-gray-400">(optional)</span></label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={form.customerAddress}
                    onChange={e => setForm(f => ({ ...f, customerAddress: e.target.value }))}
                    placeholder="Kandy"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1D1D1F] mb-1.5">Message <span className="text-gray-400">(optional)</span></label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Any specific requirements or questions..."
                  rows={2}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30" />
              </div>
            </div>

            <button type="submit"
              className="w-full bg-[#7FA86A] text-white py-3 rounded-xl font-semibold hover:bg-[#e56d00] transition-colors text-sm">
              Submit Order Request
            </button>
            <p className="text-xs text-center text-gray-400">
              By submitting, you agree to be contacted by K-Tec regarding your inquiry.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
